import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  JjamesButtonComponent, JJamesInputComponent, JJamesCardComponent,
  JJamesAlertComponent
} from 'shared-lib';
import {
  CreateParkingOperatorRequest, ParkingService, CustomerService,
  CustomerDto, OperatorGroupDto, OperatorGroupRole, OperatorType
} from '../../../../api/gen';

@Component({
  selector: 'app-operators-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    JjamesButtonComponent,
    JJamesInputComponent,
    JJamesCardComponent,
    JJamesAlertComponent
  ],
  templateUrl: './operators-create.component.html',
  styleUrl: './operators-create.component.scss'
})
export class OperatorsCreateComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private parkingService = inject(ParkingService);
  private customerService = inject(CustomerService);
  private router = inject(Router);

  createOperatorForm: FormGroup;
  isSubmitting = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  customers = signal<CustomerDto[]>([]);
  operatorGroups = signal<OperatorGroupDto[]>([]);
  loadingCustomers = signal(false);
  loadingGroups = signal(false);

  constructor() {
    this.createOperatorForm = this.formBuilder.group({
      customerId: ['', [Validators.required]],
      operatorGroupId: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
      defaultPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(50)]],
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]]
    });

    // Listen to customer selection changes
    this.createOperatorForm.get('customerId')?.valueChanges.subscribe(customerId => {
      if (customerId) {
        this.loadOperatorGroups(customerId);
      } else {
        this.operatorGroups.set([]);
        this.createOperatorForm.get('operatorGroupId')?.setValue('');
      }
    });
  }

  ngOnInit(): void {
    this.loadCustomers();
  }

  loadCustomers(): void {
    this.loadingCustomers.set(true);
    this.customerService.customerFilterPost(undefined, false, 0, 100).subscribe({
      next: (response) => {
        if (response) {
          this.customers.set(response.items ?? []);
        }
        this.loadingCustomers.set(false);
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.loadingCustomers.set(false);
      }
    });
  }

  loadOperatorGroups(customerId: string): void {
    this.loadingGroups.set(true);
    this.customerService.customerCustomerIdGet(customerId, true, false).subscribe({
      next: (response) => {
        if (response) {
          this.operatorGroups.set(response.operatorGroups ?? []);
        }
        this.loadingGroups.set(false);
      },
      error: (error) => {
        console.error('Error loading operator groups:', error);
        this.loadingGroups.set(false);
      }
    });
  }

  onSubmit(): void {
    if (this.createOperatorForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.errorMessage = null;
      this.successMessage = null;

      const formValue = this.createOperatorForm.value;
      const selectedGroup = this.operatorGroups().find(g => g.id === formValue.operatorGroupId);

      const createRequest: CreateParkingOperatorRequest = {
        customerId: formValue.customerId,
        email: formValue.email,
        defaultPassword: formValue.defaultPassword,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        operatorRole: this.mapGroupRoleToOperatorType(selectedGroup?.role)
      };

      this.parkingService.parkingOperatorPost(createRequest).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response) {
            this.successMessage = 'Parking operator created successfully!';
            setTimeout(() => {
              this.router.navigate(['/user-management']);
            }, 1500); // Show success message briefly before navigating
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error.error.title || 'An error occurred while creating the parking operator. Please try again.';
          console.error('Error creating parking operator:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.createOperatorForm);
    }
  }

  onCancel(): void {
    this.router.navigate(['/user-management']);
  }

  onClearError(): void {
    this.errorMessage = null;
  }

  onClearSuccess(): void {
    this.successMessage = null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.createOperatorForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.createOperatorForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['email']) return 'Please enter a valid email address';
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} is too short`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} is too long`;
    }
    return '';
  }

  /**
   * Maps OperatorGroupRole to OperatorType
   * OperatorGroupRole: Viewer, Member, Owner, Admin, SuperUser
   * OperatorType: Admin, Member
   * 
   * Mapping:
   * - Admin -> Admin
   * - SuperUser -> Admin  
   * - Member -> Member
   * - Owner -> Admin (for now, can be adjusted)
   * - Viewer -> Member (read-only member)
   */
  private mapGroupRoleToOperatorType(role?: OperatorGroupRole): OperatorType {
    if (!role) return OperatorType.Member;

    switch (role) {
      case OperatorGroupRole.Admin:
      case OperatorGroupRole.SuperUser:
      case OperatorGroupRole.Owner:
        return OperatorType.Admin;
      case OperatorGroupRole.Member:
      case OperatorGroupRole.Viewer:
      default:
        return OperatorType.Member;
    }
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      customerId: 'Customer',
      operatorGroupId: 'Operator Group',
      email: 'Email',
      defaultPassword: 'Default Password',
      firstName: 'First Name',
      lastName: 'Last Name'
    };
    return labels[fieldName] || fieldName;
  }
}
